import {Box} from '@mui/material';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, useContext, useState} from 'react';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {columnConfigUsers} from './TechnicalTeamTable.tableConfig';
import {TechnicalTeamTableProps} from './TechnicalTeamTable.types';
import {UsersSortFieldEnum} from 'src/enums';
import {UsersSearchRequestData} from 'src/types';
import {TechnicalTeamItemCard} from './TechnicalTeamItemCard';
import {CardList} from 'src/components/common/CardList';
import DashboardLayoutContext from 'src/layouts/DashboardLayout/DashboardLayout.context';
import {UsersFilterValue} from 'src/components/users/UsersTableAction/UsersTableActions.types';
import {UserTypeEnum} from 'src/enums/common';
import {UsersTableActions} from 'src/components/users/UsersTableAction';

export const TechnicalTeamTable: FC<TechnicalTeamTableProps> = ({
                                                                    data,
                                                                    size,
                                                                    pageNumber,
                                                                    handleUpdateTableData,
                                                                    handleChangePage,
                                                                    handleChangeRowsNumber,
                                                                    title,
                                                                    onAddClick,
                                                                    buttonText,
                                                                    onlyTitle,
                                                                    icon,
                                                                    filters,
                                                                }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'asc'});

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    // The kind of manager is the axis the strip's type tiles are on, so choosing one here clears the
    // gap chosen there - the same alternatives-not-combinations rule, from the other control.
    const handleFilter = (value: UsersFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {
            ...filters,
            notActivated: undefined,
            userTypes: value.role ? [value.role as UserTypeEnum] : undefined,
        }));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /**
     * A merge onto the filter the list is on, not a fresh object built from the toolbar's one field.
     *
     * The argument, never a state of this component's own: a copy here was a render behind when this
     * was called from the filter handler, so the first filter did nothing, the second did the first,
     * and so on. There is now only one copy to be behind.
     */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: UsersSearchRequestData
    ): UsersSearchRequestData => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field) || UsersSortFieldEnum.EMAIL,
    });

    const responsive = useContext(DashboardLayoutContext);

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <UsersTableActions
                        filters={filters}
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        isManagersRoles
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
                {responsive ? (
                    <CardList>
                        {data.content.map((item) => (
                            <TechnicalTeamItemCard
                                key={item?.id}
                                {...item}
                            />
                        ))}
                    </CardList>
                ) : (
                    <Table
                        rows={data.content}
                        hideFooter
                        columns={columnConfigUsers}
                        handleSort={handleSort}
                    />
                )}
            </TableLayout>
        </Box>
    );
};
